/**
 * ImpersonationContext — Phase 10P.
 *
 * Owns the impersonation lifecycle on the client:
 *   - startImpersonation(tenantId)  → POST /api/admin/impersonate/{tid},
 *                                     store impersonation token, refresh /me,
 *                                     redirect to /dashboard
 *   - stopImpersonation()           → POST /api/admin/impersonate/stop,
 *                                     drop the impersonation token, refresh /me,
 *                                     redirect to /platform
 *   - isImpersonating               → boolean flag derived from /me payload
 *   - impersonation                 → { actor_id, actor_email, target_tenant_id,
 *                                       target_user_id, started_at }
 *
 * The actual token swap is handled by `/lib/api.js` which prefers the
 * impersonation token over the regular access_token when both are stored.
 */
import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import api, {
  setImpersonationToken,
  getImpersonationToken,
  formatApiErrorDetail,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


const ImpersonationContext = createContext(null);


export function ImpersonationProvider({ children }) {
  const { refreshMe, user } = useAuth();
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Cache of the last successful start payload (kept for banner display
  // even before /me re-hydrates).
  const [lastStart, setLastStart] = useState(null);

  // Detect impersonation purely from server-authoritative payload
  // (user._impersonation surfaced via /api/auth/me).
  const impersonation = user?._impersonation || null;
  const isImpersonating = !!impersonation;

  // Defensive cleanup: if there's an impersonation token in localStorage
  // but the server says we're NOT impersonating (e.g. token expired),
  // drop it so we don't keep hitting 401.
  useEffect(() => {
    if (!user) return;
    const hasToken = !!getImpersonationToken();
    if (hasToken && !impersonation) {
      setImpersonationToken(null);
    }
  }, [user, impersonation]);

  const startImpersonation = useCallback(
    async (tenantId, opts = {}) => {
      if (!tenantId) return { ok: false, error: "tenant_id mancante" };
      setStarting(true);
      try {
        const { data } = await api.post(`/admin/impersonate/${tenantId}`);
        if (!data?.impersonation_token) {
          throw new Error("Token impersonation mancante nella risposta.");
        }
        setImpersonationToken(data.impersonation_token);
        setLastStart(data);
        await refreshMe();
        toast.success("Modalità assistenza attivata", {
          description: `Stai visualizzando come ${data.tenant_name || tenantId}`,
        });
        // Take the operator into the tenant's home view.
        const dest = opts.redirectTo || "/dashboard";
        window.location.assign(dest);
        return { ok: true, data };
      } catch (e) {
        const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Errore impersonation";
        toast.error(msg);
        return { ok: false, error: msg, status: e?.response?.status };
      } finally {
        setStarting(false);
      }
    },
    [refreshMe]
  );

  const stopImpersonation = useCallback(
    async (opts = {}) => {
      setStopping(true);
      try {
        // Server audit (best-effort — we still drop the local token regardless)
        await api.post("/admin/impersonate/stop").catch(() => null);
      } finally {
        setImpersonationToken(null);
        setLastStart(null);
        await refreshMe();
        toast.message("Sei tornato in modalità piattaforma.");
        if (opts.redirectTo !== null) {
          window.location.assign(opts.redirectTo || "/platform");
        }
        setStopping(false);
      }
      return { ok: true };
    },
    [refreshMe]
  );

  const value = useMemo(
    () => ({
      isImpersonating,
      impersonation,
      starting,
      stopping,
      startImpersonation,
      stopImpersonation,
      lastStart,
    }),
    [isImpersonating, impersonation, starting, stopping, startImpersonation, stopImpersonation, lastStart]
  );

  return (
    <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
  );
}


export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used inside <ImpersonationProvider>");
  return ctx;
}
